/**
 * Dashboard interface-element helpers: section resources and login preload list.
 */

import type { DashboardSectionType } from '@/types/dashboard.types';

/**
 * Maps a section type to the interface-elements endpoint name (IE name).
 * Returns null for sections that have no dedicated IE endpoint (e.g. calendar).
 * NOTE: some names use camelCase to match the backend endpoint names.
 */
export const getSectionIEResource = (sectionType: DashboardSectionType): string | null => {
  const mapping: Record<DashboardSectionType, string | null> = {
    stats: 'dashboard',
    postits: 'dashboard',
    sessions: 'sessions',
    pairs: 'pairs',
    calendar: null,
    'sessions-chart': 'dashboard',
    'resources-chart': 'dashboard',
    establishments: 'establishments',
    tutors: 'tutors',
    students: 'students',
    parents: 'parents',
    volunteers: 'volunteers',
    admins: 'admins',
    reports: 'reports',
    vscReports: 'vscReports',
    assessments: 'assessments',
    subjects: 'subjects',
    levels: 'levels',
    programs: 'programs',
    schoolYears: 'schoolYears',
    activityTypes: 'activityTypes',
    activities: 'activities',
    holidays: 'holidays',
    events: 'events',
    partners: 'partners',
    'sport-clubs': 'sport-clubs',
  };
  return mapping[sectionType] ?? null;
};

/** True when `elementId` is in the loaded interface-elements list. */
export function dashboardHasElement(elements: { elementId: string }[], elementId: string): boolean {
  return elements.some((el) => el.elementId === elementId);
}

/**
 * Interface-element names to preload at login so GlobalSearch can check {@link AuthStore.canViewInterface}.
 * Always loads the catalogue; GET `?mode=` discriminates in-person vs remote rows.
 */
export const getInitialInterfacesToLoad = (
  _rawConfig?: Record<string, unknown> | undefined,
  _mode?: 'in_person' | 'remote' | 'hybrid' | null,
): string[] => {
  const base = [
    'dashboard',
    'sidebar',
    'tutors',
    'students',
    'parents',
    'volunteers',
    'admins',
    'establishments',
    'sessions',
    'pairs',
    'calendar',
    'levels',
    'programs',
    'schoolYears',
    'activityTypes',
    'activities',
    'holidays',
    'subjects',
    'specialties',
    'reports',
    'vscReports',
    'assessments',
    'roles',
    'partners',
    'sport-clubs',
  ];

  return base;
};

/** Lucide icon name for a dashboard section type; `HelpCircle` when unknown. */
export function getSectionIcon(sectionType: string): string {
  const iconMap: Record<string, string> = {
    stats: 'BarChart3',
    postits: 'StickyNote',
    sessions: 'Calendar',
    pairs: 'Users',
    calendar: 'CalendarDays',
    'sessions-chart': 'LineChart',
    'resources-chart': 'PieChart',
    establishments: 'Building2',
    tutors: 'GraduationCap',
    students: 'Users',
    volunteers: 'Heart',
    admins: 'Shield',
    reports: 'FileText',
    vscReports: 'FileBarChart',
    assessments: 'ClipboardList',
    subjects: 'BookOpen',
    levels: 'Layers',
    programs: 'FolderTree',
    schoolYears: 'CalendarRange',
    activityTypes: 'Tag',
    activities: 'Activity',
    holidays: 'Plane',
    events: 'Ticket',
    partners: 'Building2',
    'sport-clubs': 'Trophy',
    parents: 'UsersRound',
  };

  return iconMap[sectionType] || 'HelpCircle';
}
